import { useEffect, useMemo, useState } from 'react';
import { useApp, useFetch } from '../store/app';
import { Fade, Tabs } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar } from '../components/primitives';
import { st } from '../lib/status';
import { av } from '../lib/avatar';
import { api } from '../lib/api';
import { fmtTimestamp } from '../lib/format';
import type { Onboarding as Ob, OnboardingTask } from '../../shared/types';

const SYS_BADGE: Record<string, string> = { Gusto: 'connected', Ramp: 'connected', 'Google Workspace': 'connected', 'Dropbox Sign': 'action-needed' };
const SYS_MARK: Record<string, string> = { Gusto: 'G', Ramp: 'R', 'Google Workspace': 'W', 'Dropbox Sign': 'S' };

export function Onboarding() {
  const { openDrawer, refresh } = useApp();
  const { data } = useFetch<Ob[]>('/onboarding');
  const list = data || [];
  const [tab, setTab] = useState<'in_progress' | 'complete'>('in_progress');
  const [selId, setSelId] = useState<number | null>(null);

  const visible = useMemo(() => list.filter((o) => o.status === tab), [list, tab]);
  useEffect(() => {
    // Keep a valid selection within the active tab.
    if (!visible.find((o) => o.id === selId)) setSelId(visible[0]?.id ?? null);
  }, [visible, selId]);
  const sel = list.find((o) => o.id === selId) || null;

  const toggle = async (t: OnboardingTask) => {
    await api.patch(`/onboarding/tasks/${t.id}`, { done: !t.done });
    refresh();
  };

  const groups: Record<string, OnboardingTask[]> = {};
  (sel?.tasks || []).forEach((t) => {
    (groups[t.system] ||= []).push(t);
  });

  const counts = { in_progress: list.filter((o) => o.status === 'in_progress').length, complete: list.filter((o) => o.status === 'complete').length };

  return (
    <Fade>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Tabs
          value={tab}
          onChange={(v) => setTab(v as 'in_progress' | 'complete')}
          tabs={[
            { label: `In progress (${counts.in_progress})`, value: 'in_progress' },
            { label: `Completed (${counts.complete})`, value: 'complete' },
          ]}
        />
        <div style={{ flex: 1 }} />
        <Button icon="add" onClick={() => openDrawer({ type: 'person', mode: 'create' })} style={{ padding: '6px 12px' }}>Start onboarding</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: 16, alignItems: 'start' }}>
        <Card style={{ overflow: 'hidden' }}>
          <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 14 }}>
            {tab === 'in_progress' ? 'In progress' : 'Completed'}
          </div>
          {visible.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>{tab === 'in_progress' ? 'Nothing in progress.' : 'No completed onboardings yet.'}</div>}
          {visible.map((o) => {
            const a = av(o.subject_name, o.avatar_kind);
            const dir = o.direction === 'in';
            return (
              <div key={o.id} onClick={() => setSelId(o.id)} className="cc-hover-s2 cc-clickable" style={{ display: 'flex', gap: 11, padding: '13px 16px', borderBottom: '1px solid var(--border)', background: o.id === selId ? 'var(--surface-2)' : undefined }}>
                <Avatar initials={a.in} bg={a.bg} fg={a.fg} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>{o.subject_name}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: dir ? 'var(--info-bg)' : 'var(--warning-bg)', color: dir ? 'var(--info)' : 'var(--warning)' }}>{dir ? 'Onboarding' : 'Offboarding'}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', margin: '3px 0 7px' }}>{o.role} · {o.start_date}</div>
                  {o.status === 'complete' ? (
                    <div style={{ fontSize: 11.5, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Icon name="check_circle" size={15} fill={1} color="var(--success)" />Completed {fmtTimestamp(o.completed_at)}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'var(--surface-3)', overflow: 'hidden' }}><div style={{ height: '100%', width: `${o.pct}%`, background: 'var(--accent)' }} /></div>
                      <span className="cc-mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{o.progress}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </Card>

        <Card style={{ overflow: 'hidden' }}>
          {sel ? (
            <>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{sel.subject_name} — {sel.direction === 'in' ? 'onboarding' : 'offboarding'}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>{sel.role} · {sel.start_date} · {sel.progress} done</div>
                </div>
                <div style={{ flex: 1 }} />
                {sel.status === 'complete' && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: 'var(--success-bg)', color: 'var(--success)' }}>Complete</span>}
              </div>
              <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {Object.entries(groups).map(([system, tasks]) => {
                  const badge = st(SYS_BADGE[system] || 'connected');
                  return (
                    <div key={system}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div className="cc-mono" style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, color: 'var(--text-2)' }}>{SYS_MARK[system] || system[0]}</div>
                        <span style={{ fontWeight: 600, fontSize: 13.5 }}>{system}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: badge.bg, color: badge.fg }}>{badge.label}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 4 }}>
                        {tasks.map((t) => (
                          <div key={t.id} onClick={() => toggle(t)} className="cc-hover-s2 cc-clickable" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px', borderRadius: 8 }}>
                            <Icon name={t.done ? 'check_circle' : 'radio_button_unchecked'} size={20} fill={t.done ? 1 : 0} color={t.done ? 'var(--success)' : 'var(--text-3)'} />
                            <span style={{ fontSize: 13, color: t.done ? 'var(--text-2)' : 'var(--text)' }}>{t.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Activity timeline */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 12 }}>Activity history</div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {(sel.events || []).map((e, i, arr) => (
                      <div key={e.id} style={{ display: 'flex', gap: 11 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--accent)', marginTop: 4, flex: '0 0 auto' }} />
                          {i < arr.length - 1 && <div style={{ width: 2, flex: 1, background: 'var(--border)', margin: '2px 0' }} />}
                        </div>
                        <div style={{ paddingBottom: 14, minWidth: 0 }}>
                          <div style={{ fontSize: 12.8, lineHeight: 1.4 }}>{e.message}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{e.actor} · {fmtTimestamp(e.created_at)}</div>
                        </div>
                      </div>
                    ))}
                    {!(sel.events || []).length && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No activity recorded.</div>}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Select an onboarding to view details.</div>
          )}
        </Card>
      </div>
    </Fade>
  );
}

